import { getPropertyDocumentDownloadUrl } from '../services/api';
import { formatDateLong, formatDateTime, parseInputToDate } from './date';

export function getFileName(v: any): string | null {
  if (!v) return null;
  if (typeof v !== 'object') return null;
  if (typeof v.name === 'string' && v.name.trim() !== '') return v.name;
  if (typeof v.fileName === 'string' && v.fileName.trim() !== '') return v.fileName;
  return null;
}

export function labelizeKey(key: string): string {
  const fullLower = key.toLowerCase();
  const fullOverrides: Record<string, string> = {
    cp: 'C.P.',
    cpmoral: 'C.P.',
    edad: 'Edad',
    estado: 'Estado',
    estadomoral: 'Estado',
    municipio: 'Alcaldía o municipio',
    municipiomoral: 'Alcaldía o municipio',
    rfcMoral: 'RFC',
    rfcmoral: 'RFC',
    ine: 'INE',
    fm2fm3: 'FM2/FM3',
    numeroExterior: 'Número exterior',
    numeroExteriorMoral: 'Número exterior',
    numeroInterior: 'Número interior',
    numeroInteriorMoral: 'Número interior',
    calle: 'Calle',
    calleMoral: 'Calle',
    colonia: 'Colonia',
    coloniamoral: 'Colonia',
    email: 'Correo electrónico',
    emailMoral: 'Correo electrónico',
    telefonocasa: 'Teléfono de casa',
    telefonocasamoral: 'Teléfono de casa',
    telefonoOficina: 'Teléfono de oficina',
    telefonooficinaMoral: 'Teléfono de oficina',
    celular: 'Celular',
    celularMoral: 'Celular',
    estadoCivil: 'Estado civil',
    estadoCivilRepresentante: 'Estado civil del representante',
    estadoCivilRepresentanteMoral: 'Estado civil del representante',
    fechaNacimiento: 'Fecha de nacimiento',
    fechaConstitucion: 'Fecha de constitución',
    numeroocupantes: 'Número de ocupantes',
    parentescoocupantes: 'Parentesco con los ocupantes',
    avisoprivacidad: 'Aviso de privacidad',
  };
  if (fullOverrides[fullLower]) return fullOverrides[fullLower];

  const cleaned = key
    .replace(/^docMoral_/, '')
    .replace(/^docFisica_/, '')
    .replace(/^doc_/, '')
    .replace(/_/g, ' ');

  const spaced = cleaned.replace(/([a-z])([A-Z])/g, '$1 $2');

  const lower = spaced.toLowerCase();
  const overrides: Record<string, string> = {
    cp: 'C.P.',
    rfc: 'RFC',
    ine: 'INE',
    fm2fm3: 'FM2/FM3',
    'fm2 fm3': 'FM2/FM3',
    'estado civil': 'Estado civil',
    'estado civil representante': 'Estado civil del representante',
    numero: 'Número',
    'numero exterior': 'Número exterior',
    'numero interior': 'Número interior',
    'alcaldia municipio': 'Alcaldía o municipio',
    'alcaldia o municipio': 'Alcaldía o municipio',
    'numero ocupantes': 'Número de ocupantes',
    'parentesco ocupantes': 'Parentesco con los ocupantes',
    mascotas: 'Mascotas',
    'aviso privacidad': 'Aviso de privacidad',
  };

  return overrides[lower] || spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Fecha en español largo (delega en utils/date). */
export function formatDateSpanishLong(value: string | Date): string {
  if (typeof value === 'string') {
    const d = parseInputToDate(value);
    if (!d) return value.trim() || '-';
    return formatDateLong(d);
  }
  return formatDateLong(value);
}

/** Fecha y hora (delega en utils/date). */
export function formatDateTimeSpanishLong(iso: string): string {
  const out = formatDateTime(iso);
  return out === '—' ? iso : out;
}

function tryParseToDate(raw: string): Date | null {
  return parseInputToDate(raw);
}

function isDateLikeFieldKey(key: string): boolean {
  const k = key.toLowerCase();
  return k.includes('fecha');
}

function isPhoneLikeFieldKey(key: string): boolean {
  const k = key.toLowerCase();
  return (
    k.includes('telefono') ||
    k.includes('teléfono') ||
    k.includes('celular') ||
    k.includes('phone')
  );
}

/** Teléfono MX: (55) 1234 5678 o +52 … si aplica */
export function formatPhoneDisplay(raw: string): string {
  const t = raw.trim();
  if (!t) return '-';
  const digits = t.replace(/\D/g, '');
  if (digits.length === 0) return raw;

  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)} ${digits.slice(6)}`;
  }
  if (digits.length === 12 && digits.startsWith('52')) {
    const rest = digits.slice(2);
    return `+52 (${rest.slice(0, 2)}) ${rest.slice(2, 6)} ${rest.slice(6)}`;
  }
  if (digits.length === 13 && digits.startsWith('521')) {
    const rest = digits.slice(3);
    return `+52 (${rest.slice(0, 2)}) ${rest.slice(2, 6)} ${rest.slice(6)}`;
  }

  return raw;
}

export function formatDato(v: any): string {
  if (v === null || v === undefined) return '-';
  if (typeof v === 'string') {
    const t = v.trim();
    if (t === '') return '-';
    if (t === 'on') return 'Sí';
    return t;
  }
  if (typeof v === 'number') return String(v);
  if (typeof v === 'boolean') return v ? 'Sí' : 'No';
  if (typeof v === 'object') {
    const fileName = getFileName(v);
    if (fileName) return fileName;
    const keys = Object.keys(v);
    if (keys.length === 0) return '-';
    return '[Información no legible]';
  }
  return String(v);
}

/** Formatea según el nombre del campo (fechas largas en español, teléfonos). */
export function formatDatoForField(key: string, v: any): string {
  const base = formatDato(v);
  if (base === '-' || base === '[Información no legible]') return base;
  if (typeof v !== 'string' && typeof v !== 'number') return base;

  const str = typeof v === 'number' ? String(v) : v.trim();
  if (str === '' || str === 'on') return formatDato(v);

  if (isDateLikeFieldKey(key)) {
    const d = tryParseToDate(str);
    if (d) return formatDateSpanishLong(d);
  }

  if (isPhoneLikeFieldKey(key)) {
    return formatPhoneDisplay(str);
  }

  return base;
}

const FISICA_FIELD_KEYS: string[] = [
  'nombreCompleto',
  'edad',
  'estadoCivil',
  'nacionalidad',
  'fechaNacimiento',
  'calle',
  'numeroExterior',
  'numeroInterior',
  'colonia',
  'estado',
  'municipio',
  'cp',
  'telefonoCasa',
  'telefonoOficina',
  'celular',
  'email',
  'ocupacion',
  'giroEmpresa',
  'nombreEmpresa',
  'antiguedad',
  'ingresoMensual',
  'contactoEmpresa',
  'refNombre1',
  'refTelefono1',
  'refNombre2',
  'refTelefono2',
  'refNombre3',
  'refTelefono3',
  'numeroOcupantes',
  'parentescoOcupantes',
  'mascotas',
  'avisoPrivacidad',
];

const MORAL_FIELD_KEYS: string[] = [
  'razonSocial',
  'numeroEscritura',
  'folioMercantil',
  'nacionalidadMoral',
  'numeroNotario',
  'rfcMoral',
  'fechaConstitucion',
  'nombreRepresentante',
  'nacionalidadRepresentante',
  'estadoCivilRepresentante',
  'lugarNacimientoRepresentante',
  'fechaNacimientoRepresentante',
  'calleMoral',
  'numeroExteriorMoral',
  'numeroInteriorMoral',
  'coloniaMoral',
  'estadoMoral',
  'municipioMoral',
  'cpMoral',
  'telefonoCasaMoral',
  'telefonoOficinaMoral',
  'celularMoral',
  'emailMoral',
  'refNombre1Moral',
  'refTelefono1Moral',
  'refNombre2Moral',
  'refTelefono2Moral',
  'refNombre3Moral',
  'refTelefono3Moral',
];

function normalizePersonType(s: string | undefined) {
  if (!s) return '';
  try {
    return String(s)
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase()
      .trim();
  } catch (_) {
    return String(s).toLowerCase().trim();
  }
}

export type SolicitudFormDisplayState = {
  parsedNotes: Record<string, any> | null;
  datosFormulario: Record<string, any>;
  orderedKeys: string[];
  docKeys: string[];
  docKeysWithValue: string[];
};

export function buildSolicitudFormDisplayState(notes: string | null | undefined): SolicitudFormDisplayState {
  let parsedNotes: Record<string, any> | null = null;
  if (notes && typeof notes === 'string' && notes.trim() !== '') {
    try {
      parsedNotes = JSON.parse(notes);
    } catch {
      parsedNotes = null;
    }
  }

  const datosFormulario: Record<string, any> =
    parsedNotes?.datosFormulario && typeof parsedNotes.datosFormulario === 'object' ? parsedNotes.datosFormulario : {};

  const tipoPersona = parsedNotes?.tipoPersona as string | undefined;
  const tipoPersonaNorm = normalizePersonType(tipoPersona);
  const hasTipoMoral = tipoPersonaNorm.includes('moral');
  const hasTipoFisica = tipoPersonaNorm.includes('fis') || tipoPersonaNorm.includes('fisica');

  const orderedKeys = hasTipoMoral
    ? MORAL_FIELD_KEYS
    : hasTipoFisica
      ? FISICA_FIELD_KEYS
      : Object.keys(datosFormulario).filter(
          (k) => !(k.startsWith('docMoral_') || k.startsWith('docFisica_') || k.startsWith('doc_')),
        );

  const docKeys = Object.keys(datosFormulario).filter((k) => {
    if (hasTipoMoral) return k.startsWith('docMoral_');
    if (hasTipoFisica) return k.startsWith('docFisica_');
    return k.startsWith('doc');
  });

  const docKeysWithValue = docKeys.filter((key) => {
    const value = datosFormulario[key];
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim() !== '';
    return true;
  });

  return { parsedNotes, datosFormulario, orderedKeys, docKeys, docKeysWithValue };
}

export function getDocDownloadUrl(val: any): string | null {
  if (val === null || val === undefined) return null;
  if (typeof val === 'string' && val.trim() !== '') return getPropertyDocumentDownloadUrl(val);
  if (typeof val === 'object' && typeof (val as any).url === 'string' && (val as any).url.trim() !== '') {
    return getPropertyDocumentDownloadUrl((val as any).url);
  }
  return null;
}

export function getDocMime(val: any): string {
  if (val && typeof val === 'object' && typeof (val as any).type === 'string') return (val as any).type;
  return '';
}

export function isDocImage(val: any): boolean {
  const mime = getDocMime(val);
  if (/image\//i.test(mime)) return true;
  if (typeof val === 'string' && val.toLowerCase().includes('image')) return true;
  return false;
}

export function isDocPdf(val: any): boolean {
  const mime = getDocMime(val);
  if (/pdf/i.test(mime)) return true;
  if (typeof val === 'string' && /\.pdf(\?|$)/i.test(val)) return true;
  return false;
}

export function getDocDisplayName(key: string, val: any): string {
  const fromObj = getFileName(val);
  if (fromObj) return fromObj;
  if (typeof val === 'string') {
    try {
      const last = val.split('/').pop() || val;
      return decodeURIComponent(last.split('?')[0] || last);
    } catch {
      return val;
    }
  }
  return labelizeKey(key);
}
